import Button from '@app/components/Button';
import ButtonLink from '@app/components/ButtonLink';
import ArrowForwardIcon from '@app/components/Icons/ArrowForwardIcon';
import CodeInput from '@app/forms/components/CodeInput';
import FormGroup from '@app/forms/components/FormGroup';
import { LoginContacts, LoginStep, SendTo } from '@app/pages/auth/Login/Login';
import { useStep3 } from '@app/pages/auth/Login/useStep3';
import { codeEmail } from '@app/utils/format/codeEmail';
import { codePhoneNumber } from '@app/utils/format/codePhoneNumber';
import { formatPhoneNumber } from '@app/utils/format/phoneNumber';
import { sec2minSec } from '@app/utils/format/sec2min-sec';
import { Box } from '@mui/material';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Step3.module.scss';
import { resendBox } from './Step3.sx';

interface Props {
  contacts: LoginContacts;
  setStep: Dispatch<SetStateAction<LoginStep>>;
  sendTo: SendTo;
  setSendTo: Dispatch<SetStateAction<SendTo>>;
  setContacts: Dispatch<SetStateAction<LoginContacts>>;
}

const Step3 = ({ contacts, setStep, sendTo, setSendTo, setContacts }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { email, phone } = contacts;
  const { code, setCode, isLoading, send, resend, counter } = useStep3(
    sendTo,
    email,
    phone,
    setStep,
    setSendTo,
    setContacts
  );
  const contact = sendTo === 'phone' ? codePhoneNumber(formatPhoneNumber(phone)) : codeEmail(email);
  const codeLength = 6;

  return (
    <Box className={styles.Step}>
      <h1 className="Heading HeadingH1">{t('Verification code')}</h1>
      <span className="Text">
        Please enter the verification code you got <br />
        to the {sendTo} {contact}.
      </span>

      <CodeInput length={codeLength} value={code} onChange={(v) => setCode(v)} />

      {counter > 0 ? (
        <div className={styles.Hint}>
          You can resent code after <span>{sec2minSec(counter)}</span>
        </div>
      ) : (
        <Box sx={resendBox}>
          <ButtonLink onClick={resend}>{t('Resend code')}</ButtonLink>
        </Box>
      )}

      <FormGroup>
        <div className={styles.ButtonGroup}>
          <Button
            className={styles.ButtonPrimary}
            loading={isLoading}
            disabled={code.length < codeLength}
            endIcon={!isLoading && <ArrowForwardIcon />}
            onClick={send}
          >
            {!isLoading && t('Sign in')}
          </Button>
          <Button variant="outlined" color="inherit" onClick={() => setStep(2)}>
            {t('Select another option')}
          </Button>
        </div>
      </FormGroup>
    </Box>
  );
};

export default Step3;
